import {
    ChangeDetectorRef,
    computed,
    Directive,
    ElementRef,
    HostListener,
    inject,
    input,
    Input,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    signal
} from '@angular/core';
import { TAttrOptionSelection } from '@df/catalog-ui/listing/listing';
import { IAttributeConfDataOption } from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { ProductComponentDef } from '@df/catalog-ui/product/product.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Category, type ICategoryDataV3 } from '@df/catalog/category/category';
import { EdependencyLevel } from '@df/catalog/model';
import { IProductConfigurableSelection, IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { AbService } from '@df/module-ab/ab.service';
import { injectTimeoutService, TimeoutService } from '@df/ng/timeout.service';
import { BasketService } from '@df/session/api/basket/basket.service';
import { WishlistItem } from '@df/session/api/wishlist/wishlist-item';
import { DraggingService } from '@df/ui/dragging-service';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { skip } from 'rxjs/internal/operators/skip';
import { BASKET_ADD_NOTICE_CLOSE_EVENT_NAME } from '../../../basket/basket-add-notice-modal.component';
import { IColorMapEntry, ProductDecorator } from '../../model/product/product.decorator';
import { ProductListingDecorator } from '../listing/product-listing.decorator';
import { WishlistStatus } from '../wishlist/wishlist.interface';
import { ProductService } from './product.service';

export const LISTING_IMAGE_LABEL_PATTERN = new RegExp(/base|alternative|model/);

export interface ProductEventData {
    product: ProductDecorator<IProductDataV2>;
    index?: number;
    status?: WishlistStatus;
    loading?: boolean;
    option?: IAttributeConfDataOption;
    callback?: (component: ProductAbstract) => void;
}

declare module '@df/core/service/df-event' {
    interface IEventType {
        productClosePreview: DfEventConfig<'productClosePreview', ProductEventData, void | undefined>;
        productOpenPreview: DfEventConfig<'productOpenPreview', ProductEventData, void | undefined>;
        productPreparePreview: DfEventConfig<'productPreparePreview', ProductEventData, void | undefined>;
        productWishlistAdd: DfEventConfig<'productWishlistAdd', ProductEventData, void | undefined>;
        productWishlistClosePreview: DfEventConfig<'productWishlistClosePreview', ProductEventData, void | undefined>;
        productWishlistOpenPreview: DfEventConfig<'productWishlistOpenPreview', ProductEventData, void | undefined>;
        productWishlistPreparePreview: DfEventConfig<'productWishlistPreparePreview', ProductEventData, void | undefined>;
    }
}

export const PRODUCT_CLOSE_PREVIEW_EVENT_NAME = 'productClosePreview';
export const PRODUCT_OPEN_PREVIEW_EVENT_NAME = 'productOpenPreview';
export const PRODUCT_PREPARE_PREVIEW_EVENT_NAME = 'productPreparePreview';
export const PRODUCT_WISHLIST_ADD_EVENT_NAME = 'productWishlistAdd';
export const PRODUCT_WISHLIST_CLOSE_PREVIEW_EVENT_NAME = 'productWishlistClosePreview';
export const PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME = 'productWishlistOpenPreview';
export const PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME = 'productWishlistPreparePreview';

@Directive()
export class ProductAbstract extends ProductComponentDef<ProductDecorator<IProductDataV2>> implements OnInit, OnChanges, OnDestroy {
    readonly priority = input(false);

    /**
     * If true - Block product card carousel
     */
    readonly disableCarousel = input(false);

    /**
     * Hide product price
     */
    readonly hidePrice = input(false);

    /**
     * Hide product name
     */
    readonly hideName = input(false);

    /**
     * Hide product label
     */
    readonly hideLabel = input(false);

    /**
     * Hide product video
     * @deprecated Not used in the component anymore
     */
    readonly hideVideo = input(false);

    /**
     * View base image with labels `model-base`
     */
    readonly modelView = input(false);

    /**
     * View base image with label `(base|model)-alternative`
     */
    readonly altView = input(false);

    /**
     * Wishlist item
     * Optionally passed, only on wishlist
     */
    readonly wishlistItem = input<WishlistItem | undefined>(undefined);

    /**
     * Wishlist carousel item
     * Optionally passed, only on wishlist carousel
     */
    readonly wishlistCarouselItem = input(false);

    readonly sendTracking = input(false);
    readonly category = input<Category<ICategoryDataV3> | undefined>(undefined);

    /**
     * Optional search term if showing from the search page
     */
    readonly searchTerm = input<string | undefined>(undefined);
    readonly isSearchOutlet = input(false);

    readonly isAccountPage = input(false);

    readonly listingSize = input(2);

    readonly sizes = input<string | string[] | undefined>(undefined);

    @Input()
    override listing?: ProductListingDecorator<ProductDecorator<IProductDataV2>>;

    /** @internal Currently selected color */
    private _currentColor?: number;

    /**
     * Configurable product related to currently selected product
     */
    protected currentColorProduct: ProductDecorator<IProductDataV2> | undefined;

    protected labelDiscount = false;

    protected setColourWithDelay: (color: number) => void;

    /**
     * Prepare preview
     */
    protected readonly _previewStatus = new Status();

    /**
     * Current model price
     */
    readonly currentPrice = signal(0);

    /**
     * Current model org price
     */
    readonly currentOrgPrice = signal(0);

    /**
     * Hash with selected colour to link to PP properly
     */
    readonly urlFragment = signal<string | undefined>(undefined);

    /**
     * If component is ready to show preview
     */
    private previewReady = false;

    /**
     * If preview was requested (button was clicked)
     */
    private requestedPreview = false;

    /**
     * Parent model change subscription
     */
    private parentSubscription?: Subscription;

    detectChanges$ = new Subject<void>();

    basketAdded = false;
    emailMeWhenInStock = false;

    protected basketAddNotifyTimeout?: number;
    protected abServiceSubscription?: Subscription;

    protected dfEventService = inject(DfEventService);
    protected productService = inject(ProductService);
    protected basketService = inject(BasketService);
    protected abService = inject(AbService);
    protected readonly disposableTimeoutService = injectTimeoutService();

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        elementRef: ElementRef,
        private draggingService: DraggingService,
        private cacheService: CacheService
    ) {
        super(changeDetectorRef, app, timeoutService, elementRef);

        this.setColourWithDelay = this.disposableTimeoutService.debounce(color => (this.currentColor = color), 500);
    }

    override ngOnInit() {
        this.subscriptions.add(this.app.customer.model.obs.status.pipe(skip(1)).subscribe(() => this.updatePrice()));
        this.subscriptions.add(this.detectChanges$.pipe(skip(1)).subscribe(() => this.detectChanges()));

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.model, this.listPosition)) {
                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        if (event.data.callback) {
                            event.data.callback(this);
                        }

                        this.closePreview();
                    }

                    if (event.id === PRODUCT_PREPARE_PREVIEW_EVENT_NAME) {
                        if (event.data.callback) {
                            event.data.callback(this);
                        }

                        this.loadPreview(() => {
                            this.dfEventService.dispatch({
                                id: PRODUCT_OPEN_PREVIEW_EVENT_NAME,
                                data: event.data,
                                action: () => {
                                    this.basketAdded = false;

                                    this.draggingService.lock(this);

                                    this.requestedPreview = true;

                                    this.detectChanges();
                                }
                            });
                        });
                    }

                    if (event.id === PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME) {
                        this.dfEventService.dispatch({
                            id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                            data: {
                                ...event.data,
                                loading: true
                            }
                        });

                        this.loadPreview(() => {
                            this.dfEventService.dispatch({
                                id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                                data: {
                                    ...event.data,
                                    loading: false
                                },
                                action: () => {
                                    this.basketAdded = false;

                                    this.draggingService.lock(this);

                                    this.requestedPreview = true;

                                    this.detectChanges();
                                }
                            });
                        });
                    }
                } else if (event.id.startsWith('product')) {
                    this.closePreview();
                }

                if (event.id === BASKET_ADD_NOTICE_CLOSE_EVENT_NAME) {
                    if (this.basketAddNotifyTimeout) {
                        clearTimeout(this.basketAddNotifyTimeout);
                        this.basketAddNotifyTimeout = undefined;
                    }

                    this.basketAdded = false;

                    this.closePreview();
                }
            })
        );

        this.subscriptions.add(
            this.basketService.addToBasketBeforeSubject.subscribe(() => {
                this.basketAdded = true;

                if (this.basketAddNotifyTimeout) {
                    clearTimeout(this.basketAddNotifyTimeout);
                    this.basketAddNotifyTimeout = undefined;
                }

                this.basketAddNotifyTimeout = this.disposableTimeoutService.setTimeout(() => {
                    this.basketAdded = false;

                    this.closePreview();
                }, 12000);
            })
        );

        this.abServiceSubscription = this.abService.getSubject('labelDiscount').subscribe(value => {
            if (value.variant === 'B') {
                this.labelDiscount = true;
            } else {
                this.labelDiscount = false;
            }

            this.detectChanges();
        });

        super.ngOnInit();
    }

    override ngOnDestroy() {
        if (this.basketAddNotifyTimeout) {
            clearTimeout(this.basketAddNotifyTimeout);
        }
        this.detectChanges$.complete();
        this.draggingService.release(this);
        this.parentSubscription?.unsubscribe();
        this.abServiceSubscription?.unsubscribe();

        super.ngOnDestroy();
    }

    readonly hrefLink = computed(() => {
        const urlFragment = this.urlFragment();
        const url = this.model?.data?.url;

        if (urlFragment) {
            return `${url}#${urlFragment}`;
        }

        return url;
    });

    readonly klevuSearchTrackClick = computed(() => {
        const searchTerm = this.searchTerm();

        if (searchTerm && this.model) {
            return {
                product: this.model,
                term: searchTerm
            };
        }

        return undefined;
    });

    readonly klevuCategoryProductTrackClick = computed(() => {
        const category = this.category();

        if (this.sendTracking() && category && this.model) {
            return {
                product: this.model,
                category,
                position: !!this.listPosition && this.listPosition > 0 ? this.listPosition - 1 : undefined
            };
        }

        return undefined;
    });

    get configurableSelection(): IProductConfigurableSelection | undefined {
        if (this.currentColor) {
            return {
                color: this.currentColor,
                ...this.selection
            };
        }

        return this.selection;
    }

    // Computed signals for values used multiple times in template
    readonly computedHideName = computed(() => this.hideName());
    readonly computedHidePrice = computed(() => this.hidePrice());
    readonly computedHideLabel = computed(() => this.hideLabel());
    readonly computedIsAccountPage = computed(() => this.isAccountPage());
    readonly computedIsSearchOutlet = computed(() => this.isSearchOutlet());

    // Computed signal for hide name and price together
    readonly computedHideNameAndPrice = computed(() => {
        return this.computedHideName() && this.computedHidePrice();
    });

    // Computed signal for product name
    readonly computedProductName = computed(() => {
        return this.currentColorProduct?.productName || this.model?.productName;
    });

    /**
     * List of colors to display swatches (with metadata from colors map)
     */
    readonly computedColors = computed(() => {
        const colors = this.getAllColors();
        return colors.length > 1 ? colors : undefined;
    });

    readonly computedColorsSlice = computed(() => {
        const colors = this.computedColors();

        if (!colors || colors.length <= 3) return colors;

        return colors.slice(0, 3);
    });

    // Backward compatibility getter
    get colors(): number[] | undefined {
        return this.computedColors();
    }

    private getAllColors(): number[] {
        const parent = <ProductDecorator<IProductDataV2>>this.model?.parent;

        // only when configurable product has at least 2 colour options
        if (parent.hasColorOption) {
            if (Array.isArray(parent?.data?.colorMap)) {
                const inStockOps = parent.data?.color;
                const ops = parent.data.colorMap.map(i => i.color);

                return Array.isArray(inStockOps) ? ops.filter(i => inStockOps?.includes(i)) : ops;
            }

            if (Array.isArray(parent?.data?.color)) {
                return parent.data.color;
            }
        }

        return [];
    }

    set currentColor(color: number) {
        if (color !== this._currentColor) {
            this._currentColor = color;
            this.updateColorDeps();
        }
    }

    get currentColor(): number | undefined {
        return typeof this._currentColor === 'number' ? this._currentColor : undefined;
    }

    protected override onModelChange() {
        this.parentSubscription?.unsubscribe();
        this.parentSubscription = this.model?.parent.change.pipe(skip(1)).subscribe(() => {
            this.detectChanges();
        });

        const color = this.model?.data?.color;

        if (color && typeof color === 'number') {
            // prevent unnecessary detect changes
            this._currentColor = color;
        } else {
            const colors = this.getAllColors();

            if (colors && colors.length) {
                const matchingColor = this.parent?.data?.colorMap?.find(i => i.parent === this.parent?.id)?.color;
                const color = matchingColor && colors.includes(matchingColor) ? matchingColor : colors[0];

                // prevent unnecessary detect changes
                this._currentColor = color;
            }
        }

        const selection = this.getSelection();

        if (selection) {
            this.emailMeWhenInStock =
                !!this.app.data.showOutOfStock && (!this.model?.inStock || !this.model.deepStockCheck(selection, true));
        }

        this.updateColorDeps();

        super.onModelChange();
    }

    handleDataChange(data: IProductConfigurableSelection) {
        this.selection = data;

        this.detectChanges();
    }

    handleInitChange(data: IProductConfigurableSelection) {
        this.matchExactly(data);

        this.handleDataChange(data);
    }

    handleSelectionChange(data: IProductConfigurableSelection) {
        this.matchExactly(data);
    }

    /**
     * Updates price value
     */
    protected updatePrice(detectChanges = true) {
        const colorInfo = this.getColorInfo();

        this.currentPrice.set((colorInfo ? this.getPrice(colorInfo) : this.model?.price) || 0);
        this.currentOrgPrice.set((colorInfo ? this.getOrgPrice(colorInfo) : this.model?.orgPrice) || 0);
        this.urlFragment.set(colorInfo ? `selection.color=${colorInfo.color}` : undefined);

        if (detectChanges) {
            this.detectChanges();
        }
    }

    protected getPrice(colorInfo: IColorMapEntry, orgPrice = false): number | undefined {
        const groupPrice = this.getGroupPrice(colorInfo, orgPrice);

        if (typeof groupPrice === 'number') {
            return groupPrice;
        }

        return orgPrice ? colorInfo.org_price : colorInfo.price;
    }

    protected getOrgPrice(colorInfo: IColorMapEntry): number | undefined {
        const orgPriceWithGroups = this.getPrice(colorInfo, true);
        const priceWithGroups = this.getPrice(colorInfo);
        const priceWithoutGroups = colorInfo.price;
        const orgPrice =
            priceWithoutGroups && orgPriceWithGroups
                ? Math.max(priceWithoutGroups, orgPriceWithGroups)
                : priceWithoutGroups || orgPriceWithGroups;

        if (orgPrice && priceWithGroups && orgPrice > priceWithGroups) {
            return orgPrice;
        }

        return undefined;
    }

    protected getGroupPrice(colorInfo: IColorMapEntry, orgPrice = false): number | undefined {
        const group = this.groupId;

        if (group) {
            const groupPrice = orgPrice ? colorInfo.group_org_price?.[group] : colorInfo.group_price?.[group];

            if (typeof groupPrice === 'number') {
                return groupPrice;
            }
        }

        return undefined;
    }

    protected get groupId(): number | undefined {
        return this.app.customer.model.data?.group_id;
    }

    protected updateColorDeps() {
        this.updateCurrentColorProduct();
        this.updatePrice(false);
        this.detectChanges();
    }

    /**
     * We need to load a product related to currently selected product
     * it is not the same product as current parent cause of cross-link binding
     * We identify the product using colors map information
     *
     */
    private updateCurrentColorProduct() {
        const colorInfo = this.getColorInfo();

        let colorProductId: number | undefined;

        if (colorInfo) {
            const colorOption = this.getOnlyColorOption();

            if (colorOption) {
                colorProductId = colorOption[colorInfo.color][0];
            } else {
                colorProductId = colorInfo.parent;
            }
        }

        if (colorProductId) {
            this.currentColorProduct = this.app.resources.product.get(colorProductId) as ProductDecorator<IProductDataV2>;

            if (this.currentColorProduct && !this.currentColorProduct.hasV2) {
                this.currentColorProduct?.load(2).then(() => this.detectChanges());
            }
        } else {
            this.currentColorProduct = this.model;
        }
    }

    /**
     * If product has only one configurable option and its color
     */
    private getOnlyColorOption(): Record<number, number[]> | undefined {
        const options = this.parent?.data?.options?.configurable;

        if (options && !!this.parent?.hasColorOption && Object.values(options).length === 1) {
            return options['color'];
        }

        return undefined;
    }

    /**
     * Retrieves information about color
     */
    getColorInfo(color = this.currentColor): IColorMapEntry | undefined {
        const colorMap = this.parent?.data?.colorMap || this.model?.data?.colorMap;

        return colorMap?.find(i => i.color === color) as IColorMapEntry | undefined;
    }

    ///////////////////////////////
    //
    // PREVIEW
    //
    ///////////////////////////////

    /**
     * If add to basket button can be shown
     */
    get canShowAddToBasket() {
        return this.model?.isSimple || this.canPreview;
    }

    /**
     * If preview options can be used (otherwise simple product approach is taken)
     */
    get canPreview() {
        if (this.parent?.hasSizeOption) {
            const options = this.parent.data?.options?.configurable;

            if (options) {
                const optionsQty = Object.values(options).length;

                switch (optionsQty) {
                    case 1: // size is the only option
                        return true;
                    case 2: // check if the other option is color AND its actually picked
                        return !!options?.['color'] && !!this.currentColor;
                }
            }
        }

        return false;
    }

    /**
     * Load v3 data for preview
     */
    loadPreview(callback?: () => void) {
        if (this.canPreview && !this._previewStatus.busy && !this.previewReady) {
            this.app.request.send(
                this._previewStatus,
                () => this.model?.parent.load(3, { dependencyLevel: EdependencyLevel.details }),
                () => {
                    this.previewReady = true;

                    if (callback) {
                        callback();
                    }
                }
            );
        } else if (this.previewReady && callback) {
            if (callback) {
                callback();
            }
        }
    }

    /**
     * Preview mode close cleanups
     */
    closePreview() {
        if (this.requestedPreview && !this.basketAdded) {
            this.dfEventService.dispatch({
                id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                data: {
                    product: this.model,
                    index: this.listPosition
                },
                action: () => {
                    this.draggingService.release(this);

                    this.requestedPreview = false;

                    this.detectChanges();
                }
            });
        }
    }

    @HostListener('mouseleave')
    onMouseOut() {
        if (!this._previewStatus.busy && !this.client.isS && !this.client.isM) {
            this.closePreview();
        }
    }

    @HostListener('document:touchmove', ['$event'])
    onTouchMove(event: TouchEvent) {
        if (!event.defaultPrevented) {
            this.closePreview();
        }
    }

    get shortLabel(): string | undefined {
        return this.model?.labelShort;
    }

    get labelColor(): string | undefined {
        return this.model?.labelColor;
    }

    protected getSelection(): TAttrOptionSelection | undefined {
        if (!this.listing) {
            return {};
        }

        const filterSelection = this.listing?.filterSelection || [];

        return this.cacheService.get(['filterStockSelection', ...filterSelection], () => {
            const selection: TAttrOptionSelection = {};

            this.listing?.filters.forEach(_filter => {
                if (_filter.config.type === 'attribute-option' || _filter.config.type === 'attribute' || _filter.config.type === 'stock') {
                    const filterSelection =
                        (_filter.selection as number[]) || ((_filter['options'] as Catalog.IAttributeOption[]) || []).map(i => i.value);

                    if (filterSelection && Array.isArray(filterSelection)) {
                        selection[_filter.id] = filterSelection;
                    }
                }
            });

            return selection;
        });
    }
}
